import { MaybeRefOrGetter, ref, toRef } from 'vue';
import { api } from '@/api';
import { buildOdataQuery } from '@/utils/odata';
import {
  EntityWithCommentType,
  type CreateOrUpdatePtwCommentDto,
  CreateOrUpdatePtwCommentDtoTypeEnum,
  FileMetaInfoDto,
  UpdatePtwCommentRequest,
  CommentTagType,
} from '@/api/generator';
import { ITEM_ROOT } from 'odata-query';

const useCrudForComments = (
  entityId: MaybeRefOrGetter<string | number | undefined>,
  entityType: EntityWithCommentType,
) => {
  const entityIdRef = toRef(entityId);
  // STATE
  const talkingToBackend = ref(false);

  // helper for below
  const createDto = (
    description: string,
    attachments: Array<FileMetaInfoDto>,
    tags?: CommentTagType[],
  ): CreateOrUpdatePtwCommentDto | undefined => {
    return entityIdRef.value
      ? {
          entityId: entityIdRef.value.toString(),
          entityType,
          description: description ?? null,
          tags,
          attachments,
          $type:
            CreateOrUpdatePtwCommentDtoTypeEnum.PtwPtw100AppZeroApplicationSharedCommonDtoCommentsCreateOrUpdatePtwCommentDtoPtwPtw100AppZeroApplicationShared,
        }
      : undefined;
  };

  /**
   * get all comments for an entity
   * @param entityId
   * @returns and array of comment dtos
   */
  const getCommentsForEnity = async (tags?: CommentTagType[]) => {
    talkingToBackend.value = true;

    const tagFilter = tags?.length ? { tags: { any: { [ITEM_ROOT]: { in: tags } } } } : {};

    const query = buildOdataQuery({
      filter: {
        entityId,
        ...tagFilter,
      },
    });

    const { result } = await api.commentsApi.getPtwCommentList(query);

    if (result?.value) {
      return result.value;
    }
  };

  /**
   * create
   * @param description
   * @param attachments
   * @returns
   */
  const createComment = async (
    description: string,
    attachments: Array<FileMetaInfoDto>,
    tags?: CommentTagType[],
  ) => {
    talkingToBackend.value = true;

    const dto = createDto(description, attachments, tags);

    const { result, success } = await api.commentsApi.createPtwComment({
      createOrUpdatePtwCommentDto: dto,
    });

    talkingToBackend.value = false;

    if (success) {
      return result;
    }
  };

  /**
   * update
   */
  const updateComment = async (
    id: string,
    description: string,
    attachments: Array<FileMetaInfoDto>,
    tags?: CommentTagType[],
  ) => {
    const dto = createDto(description, attachments, tags);
    const reqObj: UpdatePtwCommentRequest = {
      id,
      createOrUpdatePtwCommentDto: dto,
    };

    const { result, success } = await api.commentsApi.updatePtwComment(reqObj);
    if (success) {
      return result;
    }
  };

  const deleteComment = async (id: string) => {
    talkingToBackend.value = true;
    const { success } = await api.commentsApi.deletePtwComment({ id });
    talkingToBackend.value = false;
    return success;
  };

  return {
    getCommentsForEnity,
    createComment,
    updateComment,
    deleteComment,
    talkingToBackend,
  };
};

export default useCrudForComments;
